import BasePoint from "@/model/BasePoint";
import { getAngle, getSpacing } from "@/utils/MapUtils";
import { onMounted, onUnmounted, Ref, ref } from "vue";

export function useTouchTransform(elementId: string): {
  scale: Ref<number>;
  translateX: Ref<number>;
  translateY: Ref<number>;
  rotateAngle: Ref<number>;
  setLastScale: (mscale: number) => void;
  setMinScale: (mscale: number) => void;
  setLastTrans: () => void;
  resetTranslate: () => void;
  resetTranslateMinScale: () => void;
} {
  const element = document;
  let canvasElement: HTMLDivElement;
  let startTouchSpacing: number; //开始点击时两点间距离
  let touchStartTouches: Array<BasePoint>; //开始点击时两点间触控的点
  let clickCanvasFun: (point: BasePoint) => void;

  let onlyMove: boolean;

  let minScale = 0.1;
  const maxScale = 10;

  let lastTransX = 0;
  let lastTransY = 0;
  let lastScale = 1;
  let lastRotate = 0;

  const scale = ref(1);
  const translateX = ref(0);
  const translateY = ref(0);
  const rotateAngle = ref(0);

  let clickUpTime: number;

  const canvasClick = () => {
    //画布点击事件
    const currentTime = new Date().getTime();
    if (currentTime - clickUpTime < 150) {
      if (touchStartTouches.length == 1) {
        console.log("单击位置： ", touchStartTouches[0]);
        if (clickCanvasFun) {
          clickCanvasFun(touchStartTouches[0]);
        }
      }
    }
  };

  const findCanvasElement = () => {
    canvasElement = element.getElementById(elementId) as HTMLDivElement;
  };

  const setMinScale = (mscale: number) => {
    minScale = mscale;
  };

  const setLastScale = (mscale: number) => {
    lastScale = mscale;
  };

  const limitScale = (tempScale: number) => {
    if (tempScale < minScale) {
      tempScale = minScale;
    }
    if (tempScale > maxScale) {
      tempScale = maxScale;
    }
    return tempScale;
  };

  const getTranslateSpeedForScale = () => {
    //计算因为放大后平移的速率
    const nowScale = scale.value;
    let speed = 0;
    if (nowScale > 2) {
      const times = nowScale / 0.5;
      for (let i = 0; i < times - 2; i++) {
        speed += 0.4;
      }
    } else {
      speed = nowScale;
    }
    return speed;
  };

  const setTranslate = (movePoint: BasePoint) => {
    //屏幕的点换算成画布上的点
    const transX =
      (movePoint.x - touchStartTouches[0].x) / getTranslateSpeedForScale() +
      lastTransX;
    const transY =
      (movePoint.y - touchStartTouches[0].y) / getTranslateSpeedForScale() +
      lastTransY;

    translateX.value = transX;
    translateY.value = transY;
  };

  const setScale = (touches: BasePoint[]) => {
    //获取双指缩放角度
    const currentSpacing = getSpacing(touches[0], touches[1]);
    const tempScale = currentSpacing / startTouchSpacing; //注意整体的缩放倍率
    const curScale = lastScale * tempScale;
    const mScale = limitScale(curScale);
    scale.value = mScale;
  };

  const setRotate = (touches: BasePoint[]) => {
    //获取双指旋转角度
    const startAngle = getAngle(touchStartTouches[0], touchStartTouches[1]);
    const moveAngle = getAngle(touches[0], touches[1]);
    const rotation = moveAngle - startAngle;
    rotateAngle.value = rotation + lastRotate;
  };

  const getTouchCanvasPoint = (touches: TouchList) => {
    //屏幕的点换算成画布上的点
    const transTouchs = new Array<BasePoint>();
    for (let i = 0; i < touches.length; i++) {
      const t = {
        x: touches[i].clientX - canvasElement.getBoundingClientRect().left,
        y: touches[i].clientY - canvasElement.getBoundingClientRect().top,
      };
      // console.log("数据4： " + this.canvasLeft + " : " + this.canvasTop);
      transTouchs.push(t);
    }
    return transTouchs;
  };

  const hanldeTouchStart = (event: TouchEvent) => {
    clickUpTime = new Date().getTime();
    touchStartTouches = getTouchCanvasPoint(event.targetTouches);
    if (touchStartTouches.length == 2) {
      startTouchSpacing = getSpacing(
        touchStartTouches[0],
        touchStartTouches[1]
      );
    }
  };

  const hanldeTouchMove = (event: TouchEvent) => {
    const touches = getTouchCanvasPoint(event.targetTouches);
    if (touches.length == 1 && touchStartTouches.length == 1) {
      onlyMove = true;
      setTranslate(touches[0]);
    } else if (touches.length == 2) {
      onlyMove = false;
      setScale(touches);
      setRotate(touches);
    }
  };

  let mouseIn = false;
  let mouseDown = false;
  const OnMouseDown = (event: MouseEvent) => {
    if (event.button == 0) {
      mouseDown = true;
      touchStartTouches = [{ x: event.clientX, y: event.clientY }];
    }
  };

  const OnMouseUp = (event: MouseEvent) => {
    mouseDown = false;
    lastTransX = translateX.value;
    lastTransY = translateY.value;
  };

  const onMouseMove = (event: MouseEvent) => {
    if (mouseIn && mouseDown) {
      setTranslate({ x: event.clientX, y: event.clientY });
    } else {
      lastTransX = translateX.value;
      lastTransY = translateY.value;
    }
  };

  const onMouseOver = (event: MouseEvent) => {
    if (event.button == 0) {
      mouseIn = true;
      touchStartTouches = [{ x: event.clientX, y: event.clientY }];
    }
  };

  const onMouseOut = (event: MouseEvent) => {
    mouseIn = false;
    lastTransX = translateX.value;
    lastTransY = translateY.value;
  };

  const hanldeTouchEnd = () => {
    canvasClick();
    if (touchStartTouches.length == 1 && onlyMove) {
      lastTransX = translateX.value;
      lastTransY = translateY.value;
    }
    lastRotate = rotateAngle.value;
    lastScale = scale.value;
    onlyMove = false;
  };

  const hanldeMousewheel = (ev: WheelEvent) => {
    const curScale = scale.value - ev.deltaY / 1200;
    const mScale = limitScale(curScale);
    scale.value = mScale;
  };

  const setLastTrans = () => {
    lastTransX = translateX.value;
    lastTransY = translateY.value;
  };

  onMounted(() => {
    findCanvasElement();
    if (canvasElement) {
      canvasElement.addEventListener("touchstart", hanldeTouchStart);
      canvasElement.addEventListener("touchmove", hanldeTouchMove);
      canvasElement.addEventListener("touchend", hanldeTouchEnd);
      canvasElement.addEventListener("mousedown", OnMouseDown); //事件会在鼠标按键被按下时发生。
      canvasElement.addEventListener("mouseup", OnMouseUp); //事件会在鼠标按键被松开时发生。
      canvasElement.addEventListener("mousemove", onMouseMove); //事件会在鼠标指针移动时发生。
      canvasElement.addEventListener("mouseover", onMouseOver); //事件会在鼠标指针移动到指定的对象上时发生。
      canvasElement.addEventListener("mouseout", onMouseOut); //事件会在鼠标指针移出指定的对象时发生。
      canvasElement.addEventListener("wheel", hanldeMousewheel, {
        passive: false,
      });
      window.addEventListener("mousedown", OnMouseDown); //事件会在鼠标按键被按下时发生。
      window.addEventListener("mouseup", OnMouseUp); //事件会在鼠标按键被松开时发生。
    }
  });

  onUnmounted(() => {
    if (canvasElement) {
      canvasElement.removeEventListener("touchstart", hanldeTouchStart);
      canvasElement.removeEventListener("touchmove", hanldeTouchMove);
      canvasElement.removeEventListener("touchend", hanldeTouchEnd);
      canvasElement.removeEventListener("mousedown", OnMouseDown); //事件会在鼠标按键被按下时发生。
      canvasElement.removeEventListener("mouseup", OnMouseUp); //事件会在鼠标按键被松开时发生。
      canvasElement.removeEventListener("mousemove", onMouseMove); //事件会在鼠标指针移动时发生。
      canvasElement.removeEventListener("mouseover", onMouseOver); //事件会在鼠标指针移动到指定的对象上时发生。
      canvasElement.removeEventListener("mouseout", onMouseOut); //事件会在鼠标指针移出指定的对象时发生。
      canvasElement.removeEventListener("wheel", hanldeMousewheel);
      window.removeEventListener("mousedown", OnMouseDown); //事件会在鼠标按键被按下时发生。
      window.removeEventListener("mouseup", OnMouseUp); //事件会在鼠标按键被松开时发生。
    }
  });

  const resetTranslate = () => {
    translateX.value = 0;
    translateY.value = 0;
    scale.value = 1;
    rotateAngle.value = 0;
    lastTransX = 0;
    lastTransY = 0;
    lastScale = 1;
    lastRotate = 0;
  };

  const resetTranslateMinScale = () => {
    translateX.value = 0;
    translateY.value = 0;
    scale.value = minScale;
    rotateAngle.value = 0;
    lastTransX = 0;
    lastTransY = 0;
    lastScale = minScale;
    lastRotate = 0;
  };

  return {
    scale,
    translateX,
    translateY,
    rotateAngle,
    setLastScale,
    setMinScale,
    setLastTrans,
    resetTranslate,
    resetTranslateMinScale,
  };
}
